import { z } from 'zod';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
const PASSWORD_MESSAGE = 'Senha deve ter mínimo 8 caracteres, com maiúscula, minúscula e número';

export const passwordSchema = z
  .string()
  .min(8, PASSWORD_MESSAGE)
  .regex(PASSWORD_REGEX, PASSWORD_MESSAGE);

export const emailSchema = z.string().email('Email inválido').toLowerCase().trim();

export const phoneSchema = z
  .string()
  .regex(/^\+?[1-9]\d{10,14}$/, 'Telefone inválido (use formato +5511999999999)');

export const cpfSchema = z
  .string()
  .regex(/^\d{11}$/, 'CPF deve ter 11 dígitos numéricos');

export const cnpjSchema = z
  .string()
  .regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos numéricos');

export const cepSchema = z
  .string()
  .regex(/^\d{8}$/, 'CEP deve ter 8 dígitos numéricos');

/**
 * Endereço profissional do advogado. Exigido pela OAB e usado pra emissão de
 * notas fiscais. Cidade/UF separados (não derivados do CEP) pra resiliência
 * — ViaCEP pode estar offline no momento do cadastro.
 */
export const professionalAddressSchema = z.object({
  cep: cepSchema,
  street: z.string().min(2, 'Logradouro obrigatório').max(200),
  number: z.string().min(1, 'Número obrigatório').max(20),
  complement: z.string().max(100).optional(),
  district: z.string().min(2, 'Bairro obrigatório').max(120),
  city: z.string().min(2, 'Cidade obrigatória').max(120),
  state: z.string().length(2, 'UF com 2 letras').regex(/^[A-Z]{2}$/, 'UF em maiúsculo'),
});
export type ProfessionalAddress = z.infer<typeof professionalAddressSchema>;

export const signupUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(2, 'Nome muito curto').max(120),
  phone: phoneSchema.optional(),
});

export const signupLawyerSchema = signupUserSchema.extend({
  // Identidade profissional
  cpf: cpfSchema,
  cnpj: cnpjSchema.optional(), // sociedade de advogados ou advogado PJ
  oab: z.string().regex(/^\d{1,8}$/, 'Número da OAB inválido'),
  oabState: z
    .string()
    .length(2, 'UF deve ter 2 letras')
    .regex(/^[A-Z]{2}$/, 'UF em maiúsculo'),
  // Documentos OAB — URL externa por ora; upload nativo na Fase 2.
  // Carteira ajuda a equipe na verificação manual; certidão de regularidade
  // (CRA) é auto-verificável via CNA OAB no futuro.
  oabCardFileUrl: z.string().url('URL inválida').optional(),
  oabRegularityCertFileUrl: z.string().url('URL inválida').optional(),
  // Endereço profissional — obrigatório pela OAB (Prov. 205/2021)
  address: professionalAddressSchema,
  // Perfil profissional
  bio: z.string().min(200, 'Bio mínima de 200 caracteres — descreva sua trajetória e diferenciais').max(2000),
  specialties: z.array(z.string()).min(1, 'Escolha ao menos uma especialidade').max(15),
  tags: z
    .array(z.string().min(2).max(40))
    .max(20, 'Máximo de 20 tags')
    .optional(),
  linkedinUrl: z.string().url('URL inválida').optional(),
  professionalPhotoUrl: z
    .string()
    .url('URL inválida — use Drive/OneDrive com link público')
    .optional(),
  // Aceites e declarações — obrigatórios (literal true)
  acceptedEthicsCode: z
    .literal(true, { errorMap: () => ({ message: 'Aceite ao Código de Ética da OAB é obrigatório' }) }),
  acceptedPlatformTerms: z
    .literal(true, { errorMap: () => ({ message: 'Aceite ao Termo de Parceria é obrigatório' }) }),
  declaredNoImpediment: z
    .literal(true, { errorMap: () => ({ message: 'Declaração de não impedimento é obrigatória' }) }),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Senha obrigatória'),
});

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1),
});

export const confirmSignupSchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/, 'Código de 6 dígitos'),
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/, 'Código de 6 dígitos'),
  newPassword: passwordSchema,
});

export type SignupUserInput = z.infer<typeof signupUserSchema>;
export type SignupLawyerInput = z.infer<typeof signupLawyerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;
export type ConfirmSignupInput = z.infer<typeof confirmSignupSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
